import { Alert, Box, Chip, Grid, MenuItem, TextField, Typography } from "@mui/material";

export default function BookDetailsFields({
  form,
  updateField,
  physical,
  pdfInfo,
  paper,
  editing = false,
  errors = {},
}) {
  const fields = [
    {
      key: "author",
      label: "Autor da obra",
      required: true,
      helper: pdfInfo?.author
        ? "Sugerido pelos metadados do PDF. Confirme quem é o autor da obra."
        : "Sugerido pelo perfil, quando disponível. Pode ser diferente do responsável pela conta.",
    },
    {
      key: "publicationYear",
      label: "Ano de publicação",
      type: "number",
      required: true,
      helper: "Informe o ano editorial. A data de criação do PDF não é o ano de publicação.",
    },
    { key: "edition", label: "Edição", type: "number", required: true },
    {
      key: "language",
      label: "Idioma da obra",
      required: true,
      options: [
        ["pt-BR", "Português"],
        ["en", "Inglês"],
        ["es", "Espanhol"],
      ],
    },
    {
      key: "finish",
      label: "Acabamento",
      required: true,
      options: [
        ["Brochura", "Capa brochura"],
        ["Grampeado", "Grampeado"],
      ],
      helper: "Sugestão inicial baseada nas páginas. Confirme o acabamento do livro.",
    },
    { key: "formatType", label: "Tipo de formato", required: true, helper: "Exemplo: Capa comum." },
    {
      key: "publisher",
      label: "Selo editorial (se houver)",
      helper: "Informe o selo da obra; deixe vazio se não houver.",
    },
    {
      key: "classificationType",
      label: "Tipo de código de categorização",
      required: true,
      options: [
        ["BISAC", "BISAC"],
        ...(form.identificationMode === "isbn" ? [["ISBN", "ISBN"]] : []),
      ],
    },
    ...(form.classificationType === "BISAC"
      ? [
          {
            key: "bisacSubject",
            label: "Assunto BISAC",
            helper:
              "Revise a descrição do assunto. As categorias editoriais são selecionadas abaixo.",
          },
        ]
      : []),
  ];
  const readOnly = (label, value, helper) => (
    <TextField
      fullWidth
      label={label}
      value={value || ""}
      InputProps={{ readOnly: true }}
      disabled={editing}
      helperText={helper}
    />
  );
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={800} mb={1}>
        Ficha da obra
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Revise os dados antes de publicar. Eles serão usados no cadastro e nas características do
        livro.
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Obra: <strong>{form.title || "Preencha o título acima"}</strong>. Título e autor sugeridos
        pelo arquivo podem conter informações incorretas e devem ser conferidos.
      </Alert>
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Preço de venda (R$)"
            required={!editing}
            placeholder="Ex.: 49,90"
            error={Boolean(errors.price)}
            data-validation-error={Boolean(errors.price)}
            value={form.price || ""}
            onChange={updateField("price")}
            inputProps={{ inputMode: "decimal", maxLength: 11 }}
            helperText={
              errors.price ||
              (editing
                ? "Deixe vazio para preservar o preço atual da loja."
                : "Valor de venda do livro na loja Tray, sem o frete.")
            }
          />
        </Grid>
        {fields.map((field) => (
          <Grid key={field.key} item xs={12} md={6}>
            <TextField
              fullWidth
              label={field.label}
              required={field.required}
              type={field.type || "text"}
              select={Boolean(field.options)}
              value={form[field.key]}
              onChange={updateField(field.key)}
              error={Boolean(errors[field.key])}
              data-validation-error={Boolean(errors[field.key])}
              helperText={errors[field.key] || field.helper}
              inputProps={
                field.type === "number"
                  ? { min: field.key === "publicationYear" ? 1000 : 1, max: 9999, step: 1 }
                  : { maxLength: 100 }
              }
            >
              {field.options?.map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        ))}
      </Grid>
      {["pdf", "physical", "weightGr", "depthCm"]
        .filter((key) => errors[key])
        .map((key) => (
          <Alert key={key} severity="error" data-validation-error="true" sx={{ mt: 2 }}>
            {errors[key]}
          </Alert>
        ))}
      <Box display="flex" alignItems="center" gap={1} mt={4} mb={2} flexWrap="wrap">
        <Typography variant="subtitle2" fontWeight={800}>
          Dados físicos
        </Typography>
        <Chip size="small" label="Dimensões e páginas extraídas do PDF" />
      </Box>
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          {readOnly(
            "Dimensões (largura × altura)",
            physical.widthCm && physical.heightCm
              ? `${physical.widthCm} × ${physical.heightCm} cm`
              : "",
            "Formato detectado na primeira página do miolo."
          )}
        </Grid>
        <Grid item xs={12} md={3}>
          {readOnly("Altura (cm)", physical.heightCm, "Detectada na primeira página do PDF.")}
        </Grid>
        <Grid item xs={12} md={3}>
          {readOnly("Largura (cm)", physical.widthCm, "Detectada na primeira página do PDF.")}
        </Grid>
        <Grid item xs={12} md={4}>
          {readOnly("Páginas", physical.pages, "Contagem do arquivo do miolo.")}
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={
              editing
                ? "Peso (g)"
                : physical.weightEstimated
                ? "Peso estimado (g)"
                : "Peso informado (g)"
            }
            value={
              editing
                ? form.weightGr
                : form.weightGr === ""
                ? physical.estimatedWeightGr || ""
                : form.weightGr
            }
            error={Boolean(errors.weightGr)}
            data-validation-error={Boolean(errors.weightGr)}
            onChange={updateField("weightGr")}
            inputProps={{ inputMode: "decimal" }}
            helperText={
              editing
                ? "Informe o peso real se conhecido. Deixe vazio para preservar o peso salvo."
                : "Estimativa do miolo, sem capa nem embalagem. Informe o peso real se conhecido; apagar restaura a estimativa."
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={
              editing
                ? "Profundidade (cm)"
                : physical.depthEstimated
                ? "Profundidade estimada (cm)"
                : "Profundidade informada (cm)"
            }
            value={
              editing
                ? form.depthCm
                : form.depthCm === ""
                ? physical.estimatedDepthCm || ""
                : form.depthCm
            }
            error={Boolean(errors.depthCm)}
            data-validation-error={Boolean(errors.depthCm)}
            onChange={updateField("depthCm")}
            inputProps={{ inputMode: "decimal" }}
            helperText={
              editing
                ? "Informe a profundidade real se conhecida. Deixe vazio para preservar a medida salva."
                : "Estimativa pela espessura do papel, sem a capa. Informe a medida real se conhecida; apagar restaura a estimativa."
            }
          />
        </Grid>
      </Grid>
      <Typography variant="caption" display="block" mt={2} color="text.secondary">
        Papel selecionado: {paper?.paperLabel || paper?.label || "não informado"}. O peso físico não
        pode ser extraído diretamente do PDF.
      </Typography>
    </Box>
  );
}
